/** INTERNET PURCHASES (2026-10-17) - online software payments (today: Gas Tracker subscriptions), each its own ticket in
 *  the 'online' category, kept apart from physical / field work: never on the Tickets list, Kanban, Schedule or Map.
 *  Each one arrives already invoiced in ERPNext and paid by card ("billed automatically").
 *  API: GET /api/online-purchases (backend/app/routers/internal_online.py). Concept design standard. */
import { useCallback, useEffect, useState } from 'react';
import { Box, Chip, CircularProgress, IconButton, InputAdornment, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Refresh as RefreshIcon, Search as SearchIcon, ShoppingCart as CartIcon } from '@mui/icons-material';
import api from '@/services/api';

const NAVY = '#0D2233';
type Row = { id: number; ticket_number: string; created_at?: string; title: string; customer?: string; amount?: number; source?: string;
  period_start?: string; period_end?: string; erp_invoice?: string; erp_payment?: string; erp_customer?: string; invoice_status?: string; status?: string };
const money = (n?: number) => (n == null ? '' : n.toLocaleString('en-US', { style: 'currency', currency: 'USD' }));
const day = (s?: string) => (s ? new Date(s).toLocaleDateString() : '');

export default function InternetPurchasesPage() {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const [rows, setRows] = useState<Row[] | null>(null);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [err, setErr] = useState('');
  const load = useCallback(() => {
    setRows(null); setErr('');
    api.get('/online-purchases', { params: { search: q || undefined, page_size: 200 } })
      .then((r: any) => { setRows(r.data.items || []); setTotal(r.data.total || 0); })
      .catch((e: any) => { setRows([]); setErr(e?.response?.data?.detail || 'Could not load internet purchases'); });
  }, [q]);
  useEffect(() => { const h = setTimeout(load, 250); return () => clearTimeout(h); }, [load]);
  const sum = (rows || []).reduce((s, r) => s + (r.amount || 0), 0);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
        <CartIcon sx={{ color: NAVY }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: NAVY, flex: 1 }}>Internet purchases</Typography>
        <Tooltip title="Refresh"><IconButton onClick={load}><RefreshIcon /></IconButton></Tooltip>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Online software purchases (Gas Tracker subscriptions) - kept apart from field work. Each one is invoiced in ERPNext and paid by card
        automatically; the customer is in the ERPNext group "Software / Internet Customers".
      </Typography>
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField size="small" placeholder="Search customer, ticket, invoice" value={q} onChange={e => setQ(e.target.value)} sx={{ flex: 1 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
          <Typography variant="body2"><b>{total}</b> purchase{total === 1 ? '' : 's'} · <b>{money(sum)}</b> shown</Typography>
        </Stack>
      </Paper>
      {err && <Typography color="error" sx={{ mb: 2 }}>{err}</Typography>}
      {rows === null ? <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
        : rows.length === 0 ? <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No internet purchases yet.</Typography>
        : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Date</TableCell><TableCell>Customer</TableCell>{!phone && <TableCell>Ticket</TableCell>}<TableCell align="right">Amount</TableCell>
              {!phone && <TableCell>Period</TableCell>}{!phone && <TableCell>ERPNext</TableCell>}<TableCell>Status</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.id} hover>
                  <TableCell>{day(r.created_at)}</TableCell>
                  <TableCell><b>{r.customer}</b><Typography variant="caption" display="block" color="text.secondary">{r.title}</Typography></TableCell>
                  {!phone && <TableCell>{r.ticket_number}</TableCell>}
                  <TableCell align="right">{money(r.amount)}</TableCell>
                  {!phone && <TableCell>{r.period_start ? `${day(r.period_start)} - ${day(r.period_end)}` : ''}</TableCell>}
                  {!phone && <TableCell><Typography variant="caption" display="block">{r.erp_invoice}</Typography><Typography variant="caption" color="text.secondary">{r.erp_payment}</Typography></TableCell>}
                  <TableCell><Chip size="small" color={r.invoice_status === 'paid' ? 'success' : 'default'} label={r.invoice_status === 'paid' ? 'Billed · paid' : (r.invoice_status || r.status || '')} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
